<template>
  <div class="ge-page">
    <Navbar />
    <div class="ge-container">
      <h1>Groupes d'Entraide</h1>

      <el-card v-if="canCreateGE" class="create-ge-card" shadow="always">
        <el-button type="primary" @click="toggleCreateForm">
          {{ showCreateForm ? 'Annuler' : "Créer un Groupe d'Entraide" }}
        </el-button>

        <div v-if="showCreateForm" class="create-form-section">
          <h3>Nouveau Groupe d'Entraide</h3>
          <el-form label-position="top" @submit.prevent>
            <el-form-item label="Nom du groupe">
              <el-input v-model="newGE.name" placeholder="Nom du groupe" />
            </el-form-item>
            <el-form-item label="Description">
              <el-input v-model="newGE.description" type="textarea" :rows="3" placeholder="Description" />
            </el-form-item>
            <el-form-item label="Thématique">
              <el-select v-model="newGE.interestId" placeholder="Choisir une thématique" style="width: 100%;">
                <el-option
                  v-for="interest in interests"
                  :key="interest.id"
                  :label="interest.name"
                  :value="interest.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="Modérateurs">
              <el-checkbox-group v-model="newGE.moderatorIds">
                <el-checkbox
                  v-for="mod in moderatorUsers"
                  :key="mod.id"
                  :label="mod.id"
                >
                  {{ mod.username }} ({{ mod.status }})
                </el-checkbox>
              </el-checkbox-group>
            </el-form-item>
            <el-form-item>
              <el-button type="success" @click="createGE">Créer</el-button>
            </el-form-item>
          </el-form>
        </div>
      </el-card>

      <div v-if="loading" class="loading">
        <el-skeleton :rows="3" animated />
      </div>

      <div v-else class="ge-list">
        <el-card v-for="ge in groupesEntraide" :key="ge.id" class="ge-card" shadow="always">
          <template #header>
            <div class="ge-header-el">
              <h2>{{ ge.name }}</h2>
              <el-tag type="primary">{{ ge.interest?.name }}</el-tag>
            </div>
          </template>

          <p class="ge-description">{{ ge.description }}</p>
          <div class="ge-moderators">
            <strong>Modérateurs :</strong>
            <el-tag
              v-for="mod in ge.moderators"
              :key="mod.id"
              size="small"
              class="mod-tag"
            >
              <UserLink :username="mod.username" />
            </el-tag>
          </div>

          <template #footer>
            <div class="ge-actions">
              <el-button @click="viewGE(ge.id)">Voir le groupe</el-button>
              <el-button
                v-if="isLoggedIn && !getMembershipStatus(ge.id)"
                type="primary"
                @click="requestMembership(ge.id)"
              >
                Demander à rejoindre
              </el-button>
              <el-tag v-else-if="getMembershipStatus(ge.id) === 'PENDING'" type="warning">
                Demande en attente
              </el-tag>
              <template v-else-if="getMembershipStatus(ge.id) === 'APPROVED'">
                <el-tag type="success">Membre</el-tag>
                <el-button type="danger" size="small" @click="leaveGE(ge.id)">Quitter</el-button>
              </template>
              <el-tag v-else-if="getMembershipStatus(ge.id) === 'REJECTED'" type="danger">
                Refusé
              </el-tag>
            </div>
          </template>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue'
import UserLink from '@/components/UserLink.vue'
import api from '@/api';
import { notifySuccess, notifyWarning, notifyError, confirmAction } from '@/notify'

export default {
  name: 'GroupesEntraide',
  components: { Navbar, UserLink },

  data() {
    return {
      groupesEntraide: [],
      interests: [],
      myMemberships: [],
      userId: null,
      userStatus: null,
      isLoggedIn: false,
      loading: true,
      showCreateForm: false,
      newGE: {
        name: '',
        description: '',
        interestId: '',
        moderatorIds: [],
      },
      moderatorUsers: [],
    }
  },

  computed: {
    canCreateGE() {
      return this.isLoggedIn && (this.userStatus === 'ADMIN' || this.userStatus === 'MODERATOR')
    },
  },

  mounted() {
    this.checkSession()
    this.fetchGEs()
    this.fetchInterests()
    document.body.style.backgroundColor = 'rgba(21, 109, 123)'
  },

  methods: {
    async checkSession() {
      try {
        const res = await api.get('/session/me', { withCredentials: true })
        if (res.status === 200) {
          this.isLoggedIn = true
          this.userId = res.data.id
          this.userStatus = res.data.status
          this.fetchMyMemberships()
        }
      } catch (err) {
        this.isLoggedIn = false
      }
    },

    async fetchGEs() {
      try {
        const res = await api.get('/ge', { withCredentials: true })
        this.groupesEntraide = res.data.groupesEntraide || []
      } catch (err) {
        console.error('Error fetching GE:', err)
      } finally {
        this.loading = false
      }
    },

    async fetchInterests() {
      try {
        const res = await api.get('/interest/getAllInterest', { withCredentials: true })
        this.interests = res.data.allInterests || []
      } catch (err) {
        console.error('Error fetching interests:', err)
      }
    },

    async fetchMyMemberships() {
      try {
        this.myMemberships = []
      } catch (err) {
        console.error(err)
      }
    },

    getMembershipStatus(geId) {
      const ge = this.groupesEntraide.find((g) => g.id === geId)
      if (!ge || !ge.members) return null
      const membership = ge.members.find((m) => m.userId === this.userId)
      return membership ? membership.status : null
    },

    async requestMembership(geId) {
      try {
        await api.post(`/ge/${geId}/membership-request`, {}, { withCredentials: true })
        notifySuccess('Demande envoyée !')
        this.fetchGEs()
      } catch (err) {
        notifyError(err, "Erreur lors de la demande")
      }
    },

    async leaveGE(geId) {
      if (!(await confirmAction("Voulez-vous vraiment quitter ce groupe d'entraide ?"))) return
      try {
        await api.delete(`/ge/${geId}/membership`, { withCredentials: true })
        notifySuccess('Vous avez quitté le groupe.')
        this.fetchGEs()
      } catch (err) {
        notifyError(err, "Erreur lors de la sortie du groupe")
      }
    },

    toggleCreateForm() {
      this.showCreateForm = !this.showCreateForm
      if (this.showCreateForm) {
        this.fetchModerators()
      }
    },

    async fetchModerators() {
      try {
        const res = await api.get('/search/moderators', { withCredentials: true })
        this.moderatorUsers = res.data.users || []
      } catch (err) {
        console.error('Error fetching moderators:', err)
      }
    },

    async createGE() {
      // Validation côté client (le serveur exige les mêmes champs et répondrait 400)
      if (!(this.newGE.name || '').trim()) {
        notifyWarning('Le nom du groupe est requis')
        return
      }
      if (!this.newGE.interestId) {
        notifyWarning('Veuillez choisir une thématique')
        return
      }
      if (this.newGE.moderatorIds.length === 0) {
        notifyWarning('Veuillez choisir au moins un modérateur')
        return
      }
      try {
        const payload = {
          name: this.newGE.name,
          description: this.newGE.description,
          interestId: this.newGE.interestId,
          moderatorIds: this.newGE.moderatorIds,
        }
        await api.post('/ge', payload, { withCredentials: true })
        notifySuccess("Groupe d'Entraide créé !")
        this.showCreateForm = false
        this.newGE = { name: '', description: '', interestId: '', moderatorIds: [] }
        this.fetchGEs()
      } catch (err) {
        notifyError(err, "Erreur lors de la création")
      }
    },

    viewGE(id) {
      this.$router.push({ name: 'GroupeEntraideDetail', params: { geId: id } })
    },
  },
}
</script>

<style scoped>
.ge-page {
  background-color: rgba(21, 109, 123);
  min-height: 100vh;
  padding-top: 60px;
  color: white;
}

.ge-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
}

.ge-container h1 {
  text-align: center;
  margin-bottom: 30px;
}

.create-ge-card {
  margin-bottom: 30px;
}

.create-form-section {
  margin-top: 20px;
}

.create-form-section h3 {
  margin-top: 0;
  margin-bottom: 16px;
  color: #303133;
}

.ge-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ge-card h2 {
  margin: 0;
  color: #303133;
}

.ge-header-el {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ge-description {
  color: #606266;
  margin-bottom: 12px;
}

.ge-moderators {
  margin-bottom: 12px;
  color: #606266;
  font-size: 14px;
}

.mod-tag {
  margin-left: 6px;
}

.ge-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.loading {
  margin-top: 40px;
}
</style>
